import { forwardRef, useEffect, useRef, useState } from 'react';
import type { ButtonHTMLAttributes, MouseEvent as ReactMouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useSettingsStore } from '@/lib/settings-store';
import { useVoiceCapture } from '@/hooks/useVoiceCapture';
import { MIC_FLASH_EVENT } from '@/lib/haptics';
import { enhancedVoiceAllowed } from '@/lib/core-cutover';
import AudioMeter from './AudioMeter';
import styles from './MicButton.module.css';

type Size = 'sm' | 'md' | 'lg' | 'xl';

interface MicButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type' | 'children'> {
  size?: Size;
  isListening: boolean;
  isTranscribing?: boolean;
  /**
   * When true (default on size="lg"), a slow breathing animation plays in the
   * idle state. Suppressed automatically while listening or transcribing.
   */
  breathe?: boolean;
  /**
   * When true, renders a status pill below the button while active, with
   * a localized stop cue during recording. Used on the editor mics.
   * PrayPage renders its own status and leaves this off.
   */
  showLabel?: boolean;
  /** Home opts in to a visual stop cue inside its large button. */
  showStopCueInside?: boolean;
  /**
   * Accessible label for the button. Caller should localize and vary by state.
   */
  ariaLabel: string;
}

const SIZE_CLASS: Record<Size, string> = {
  sm: styles.sizeSm,
  md: styles.sizeMd,
  lg: styles.sizeLg,
  xl: styles.sizeXl,
};

const ICON_PX: Record<Size, number> = { sm: 14, md: 18, lg: 26, xl: 42 };

/**
 * Shared mic button used across every voice-capture surface in the app. Owns
 * the active/transcribing/enhanced visuals so all surfaces stay aligned —
 * styling changes here propagate everywhere automatically.
 *
 * Positioning is the caller's responsibility: MicButton only renders the
 * circular button (and optionally an inline label pill when showLabel is
 * true). Wrap it with whatever layout your surface needs.
 */
const MicButton = forwardRef<HTMLButtonElement, MicButtonProps>(function MicButton(
  {
    size = 'md',
    isListening,
    isTranscribing = false,
    breathe,
    showLabel = false,
    showStopCueInside = false,
    ariaLabel,
    className,
    disabled,
    onMouseDown,
    onClick,
    ...rest
  },
  ref,
) {
  const { t } = useTranslation();
  // Subscribe so the pill label swaps live when fallback flips mid-session
  // (e.g. network drops while mic is idle, next tap uses device mic).
  // Also drives the "enhanced" ring. The deployment opt-in is independent
  // from runtime fallback state, so a null fallback alone is insufficient.
  const fallbackReason = useSettingsStore((s) => s.fallbackReason);
  const enhancedVoiceEnabled = enhancedVoiceAllowed();
  const enhanced = enhancedVoiceEnabled && fallbackReason === null;
  const shouldBreathe = breathe ?? (size === 'lg' || size === 'xl');
  const iconSize = ICON_PX[size];
  const active = isListening || isTranscribing;
  const insideStopCue = showStopCueInside && isListening && !isTranscribing;

  // 2026-10-30: the former queue soft cap (over-cap aria-disabled + toast)
  // was removed. Old recordings waiting in the queue never block the mic;
  // only admission (an earlier capture still finishing) does, below.

  // Audio level for the meter rendered in the inline label pill. Only
  // read when we will actually render the meter; getLevel itself is
  // cheap (returns a ref value) but we keep the consumer code minimal.
  const { getLevel, admissionBusy, isAdmissionBusy, getRefusalCount } = useVoiceCapture();

  // R4 busy feedback (testimony plan rev 4, section 8.7): while an earlier
  // capture is still finishing, an idle mic reads as unavailable, and a tap it
  // refuses says why instead of doing nothing. The active capture's own mic
  // (listening or writing) keeps its Stop exactly as before.
  const admissionBlocked = admissionBusy && !active;
  const effectivelyDisabled = !!disabled || admissionBlocked;
  const [busyNoticeId, setBusyNoticeId] = useState<number | null>(null);
  const busyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (busyTimerRef.current) clearTimeout(busyTimerRef.current);
  }, []);
  const showBusyNotice = () => {
    if (busyTimerRef.current) clearTimeout(busyTimerRef.current);
    setBusyNoticeId((prev) => (prev == null ? 1 : prev + 1));
    busyTimerRef.current = setTimeout(() => {
      busyTimerRef.current = null;
      setBusyNoticeId(null);
    }, 5000);
  };
  // Shown only while the refusal still holds; clears itself once admission frees.
  const busyNoticeVisible = busyNoticeId != null && admissionBusy;

  // Visual-flash subscriber per haptics.ts pulses. Only attached when
  // this MicButton is the active-listening instance (R5 in the B.1
  // ratification): isListening flips false on every other instance, so
  // their listeners never attach and the gold flash never fires for
  // them. The 150 ms timeout matches the .flashOverlay CSS animation
  // duration so the state clears as the visual completes.
  // `flashId` is null when no flash is active. When a pulse fires it
  // becomes a monotonically increasing number, used as the conditional
  // span's `key` so back-to-back pulses remount the element and re-run
  // the CSS animation cleanly. Single state variable keeps the render
  // pure and avoids the react-hooks/refs lint trap of reading ref.current
  // during render.
  const [flashId, setFlashId] = useState<number | null>(null);
  useEffect(() => {
    if (!isListening) return;
    let active = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const onFlash = () => {
      if (!active) return;
      setFlashId((prev) => (prev == null ? 1 : prev + 1));
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        if (!active) return;
        setFlashId(null);
        timer = null;
      }, 150);
    };
    window.addEventListener(MIC_FLASH_EVENT, onFlash);
    return () => {
      active = false;
      if (timer) clearTimeout(timer);
      window.removeEventListener(MIC_FLASH_EVENT, onFlash);
      // Clear any lingering flash on unmount or when isListening flips
      // false so the next session starts in a clean state.
      setFlashId(null);
    };
  }, [isListening]);

  const listeningLabel =
    !enhancedVoiceEnabled
      ? t('voice.fallbackStandard')
      : fallbackReason === 'offline'
      ? t('voice.fallbackOffline')
      : fallbackReason === 'service'
      ? t('voice.fallbackService')
      : fallbackReason === 'unauthenticated'
      ? t('voice.fallbackUnauthenticated')
      : t('pray.listening');

  const classes = [
    styles.micButton,
    SIZE_CLASS[size],
    insideStopCue ? styles.insideStopCueActive : '',
    isListening ? styles.listening : '',
    isTranscribing ? styles.transcribing : '',
    enhanced ? styles.enhanced : '',
    shouldBreathe && !active && !admissionBlocked ? styles.breathe : '',
    admissionBlocked ? styles.busy : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  // HTML `disabled` is passed only for the existing `disabled` prop (e.g.
  // isSaving); admission uses aria-disabled + onClick gating so a refused
  // tap can still explain itself with the busy notice.
  const handleClick = (e: ReactMouseEvent<HTMLButtonElement>) => {
    // An idle mic never asks to start while admission would refuse. A mic
    // whose own capture is still writing calls through (its caller decides
    // what a tap means); a start that admission refuses there is caught by
    // the refusal count and explained the same way.
    if (!active && isAdmissionBusy()) {
      showBusyNotice();
      return;
    }
    const refusalsBefore = getRefusalCount();
    onClick?.(e);
    if (getRefusalCount() !== refusalsBefore) showBusyNotice();
  };

  const busyNotice = busyNoticeVisible ? (
    <span
      key={busyNoticeId}
      className={showLabel ? styles.busyPill : styles.busyBubble}
      role="status"
      aria-live="polite"
    >
      {t('voice.finishingLast')}
    </span>
  ) : null;

  const button = (
    <button
      {...rest}
      ref={ref}
      type="button"
      className={classes}
      disabled={disabled}
      aria-disabled={effectivelyDisabled || undefined}
      aria-label={ariaLabel}
      aria-pressed={isListening}
      onClick={handleClick}
      // iOS: prevent the tap from stealing focus from an adjacent textarea,
      // which otherwise eats the click event.
      onMouseDown={(e) => {
        e.preventDefault();
        onMouseDown?.(e);
      }}
    >
      <svg
        className={styles.icon}
        width={iconSize}
        height={iconSize}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={size === 'xl' ? 1.4 : size === 'lg' ? 1.6 : 2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
        <line x1="12" y1="19" x2="12" y2="23" />
        <line x1="8" y1="23" x2="16" y2="23" />
      </svg>
      {insideStopCue && (
        <span className={styles.insideStopCue} aria-hidden="true">{t('voice.tapToStop')}</span>
      )}
      {isListening && (size === 'lg' || size === 'xl') && (
        <span className={styles.ripple} aria-hidden="true" />
      )}
      {/* Brief gold pulse triggered by haptics.ts pulse* dispatches.
          The radial overlay sits above the SVG so the existing listening
          keyframe (box-shadow on the button itself) doesn't fight a
          competing transform animation. Re-mounted on each flash via the
          `flashing` state so the CSS animation re-runs cleanly. */}
      {flashId != null && <span key={flashId} className={styles.flashOverlay} aria-hidden="true" />}
      {/* Unlabelled mics (the home hero) carry the busy notice inside the
          button's own positioning box, so no caller layout changes. */}
      {!showLabel && busyNotice}
    </button>
  );

  if (!showLabel) return button;

  // Wrap in an inline-flex so the pill sits immediately to the right of the
  // button. Idle state renders just the button (no pill occupying layout
  // space), so consumers' layouts don't shift when recording starts.
  return (
    <span className={styles.micWithLabel}>
      {button}
      {active && (
        <span
          className={`${styles.labelPill} ${
            isTranscribing ? styles.labelPillWriting : ''
          }`}
          role="status"
          aria-live="polite"
        >
          <span className={styles.labelDot} aria-hidden="true" />
          {/* Meter only renders during listening — transcribing has no
              audio source to visualize, only the pulsing dot. */}
          {isListening && <AudioMeter getLevel={getLevel} bars={5} />}
          <span className={styles.labelText}>
            <span>{isListening ? listeningLabel : t('voice.writing')}</span>
            {isListening && !isTranscribing && (
              <span className={styles.stopHint}>{t('voice.tapToStop')}</span>
            )}
          </span>
        </span>
      )}
      {busyNotice}
    </span>
  );
});

export default MicButton;
